import { formatMagnitude } from '@dawn/core/utils';
import {
  SENSITIVITY_EXTERNAL_COLOR,
  SENSITIVITY_TIERS,
  SENSITIVITY_UNCLASSIFIED_COLOR,
  SENSITIVITY_UNCLASSIFIED_ID,
  SENSITIVITY_UNCLASSIFIED_LABEL,
  sensitivityLevelFor,
  type SensitivityTierId,
} from '@dawn/feature-config/widgets';
import { SensitivityBody, type SensitivityBodyRow } from '@dawn/ui';

import type { WidgetConfig } from '@/config/dashboard.types';
import { useDrillToAccessDiscovery } from '@/hooks/use-drill-to-access-discovery';
import { USER_TYPE_BY_KIND } from '@/lib/access-legitimacy-scope';

import { WidgetShell } from '../WidgetShell';

/**
 * Inline mirror of the dashboard API's DataAccessBySensitivityData shape.
 * Kept local to honor the "no shared contract package" deferral (design §5 #11).
 */
interface SensitivityRowData {
  id: SensitivityTierId;
  label: string;
  files: number;
  hi: number;
  nhi: number;
  external: number;
  filesPercent: number;
  isHighlighted?: boolean;
}

interface SensitivityResponseData {
  totalFiles: number;
  totalHI: number;
  totalNHI: number;
  totalExternal: number;
  rows: SensitivityRowData[];
  /** Optional: an API older than this bundle does not publish the remainder. */
  unclassifiedFiles?: number;
  unclassifiedPercent?: number;
  /** The card's cutoff; a drill ends the grid there. Absent on an older API. */
  asOf?: string;
}

const REQUIRED_TIER_IDS = new Set<string>(SENSITIVITY_TIERS.map((t) => t.id));

function narrowSensitivityData(input: unknown): SensitivityResponseData | null {
  if (input === null || typeof input !== 'object') return null;
  const candidate = input as Record<string, unknown>;
  if (!Array.isArray(candidate.rows)) return null;
  if (candidate.rows.length !== REQUIRED_TIER_IDS.size) return null;
  const seenIds = new Set<string>();
  for (const row of candidate.rows as unknown[]) {
    if (row === null || typeof row !== 'object') return null;
    const r = row as Record<string, unknown>;
    if (typeof r.files !== 'number') return null;
    if (typeof r.hi !== 'number') return null;
    if (typeof r.nhi !== 'number') return null;
    if (typeof r.external !== 'number') return null;
    if (typeof r.filesPercent !== 'number') return null;
    if (typeof r.id !== 'string' || !REQUIRED_TIER_IDS.has(r.id)) return null;
    if (typeof r.label !== 'string') return null;
    seenIds.add(r.id);
  }
  if (seenIds.size !== REQUIRED_TIER_IDS.size) return null;
  if (typeof candidate.totalFiles !== 'number') return null;
  if (typeof candidate.totalHI !== 'number') return null;
  if (typeof candidate.totalNHI !== 'number') return null;
  if (typeof candidate.totalExternal !== 'number') return null;
  // Absent on an older API and rendered as no slice; present but malformed is a
  // shape violation, since a wrong remainder misreads worse than a missing one.
  const hasUnclassified =
    candidate.unclassifiedFiles !== undefined || candidate.unclassifiedPercent !== undefined;
  if (hasUnclassified) {
    if (typeof candidate.unclassifiedFiles !== 'number') return null;
    if (typeof candidate.unclassifiedPercent !== 'number') return null;
  }
  if (candidate.asOf !== undefined && typeof candidate.asOf !== 'string') return null;
  return candidate as unknown as SensitivityResponseData;
}

export function SensitivityWidget({ config }: { config: WidgetConfig }): JSX.Element {
  const drillToAccessDiscovery = useDrillToAccessDiscovery(config.id);

  return (
    <WidgetShell
      config={config}
      renderData={(envelopeData) => {
        const data = narrowSensitivityData(envelopeData);
        if (!data) return null;
        // Every drill ends the grid's window at this card's cutoff and, as the card does,
        // counts analysed files only.
        const drill = (link: Parameters<typeof drillToAccessDiscovery>[0]) =>
          drillToAccessDiscovery({ ...link, asOf: data.asOf, analysedOnly: true });

        const rows: SensitivityBodyRow[] = data.rows.map((r) => ({
          id: r.id,
          label: r.label,
          files: r.files,
          hi: r.hi,
          nhi: r.nhi,
          external: r.external,
          filesPercent: r.filesPercent,
          isHighlighted: r.isHighlighted,
        }));

        return (
          <SensitivityBody
            totalFiles={data.totalFiles}
            totalHI={data.totalHI}
            totalNHI={data.totalNHI}
            totalExternal={data.totalExternal}
            rows={rows}
            tiers={SENSITIVITY_TIERS}
            externalColor={SENSITIVITY_EXTERNAL_COLOR}
            // The remainder is not a tier, but it drills like one (into the
            // grid's no-level events); no relation measures identities for it.
            extraRow={
              data.unclassifiedFiles !== undefined && data.unclassifiedPercent !== undefined
                ? {
                    id: SENSITIVITY_UNCLASSIFIED_ID,
                    label: SENSITIVITY_UNCLASSIFIED_LABEL,
                    files: data.unclassifiedFiles,
                    filesPercent: data.unclassifiedPercent,
                    color: SENSITIVITY_UNCLASSIFIED_COLOR,
                    selectable: true,
                  }
                : undefined
            }
            formatNumber={formatMagnitude}
            onTierSelect={(id) => {
              const sensitivity = sensitivityLevelFor(id);
              if (sensitivity) drill({ sensitivity });
            }}
            onUserTypeClick={(id, kind) => {
              const sensitivity = sensitivityLevelFor(id);
              if (sensitivity) {
                drill({ sensitivity, userTypes: [USER_TYPE_BY_KIND[kind]] });
              }
            }}
            onTotalUserTypeClick={(kind) => drill({ userTypes: [USER_TYPE_BY_KIND[kind]] })}
            className="mt-2"
          />
        );
      }}
    />
  );
}
